/**
 * How PracticeOS looks on this computer: Classic or Liquid Glass, and the size
 * of its text. Each is one attribute on <html> -- data-look, data-text --
 * read before the first paint so the app never flashes the other one, and
 * kept in this browser, since it is a matter of the screen and the eyes in
 * front of it rather than of the account.
 */

import { useEffect, useState } from "react";

import "./glass.css";

export type Look = "classic" | "glass";
export type TextSize = "standard" | "large" | "largest";

const LOOK_KEY = "pos-look";
const TEXT_KEY = "pos-text-size";

const read = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private browsing: it lasts until the window closes.
  }
};

export const storedLook = () => read<Look>(LOOK_KEY, ["classic", "glass"], "classic");
export const storedTextSize = () => read<TextSize>(TEXT_KEY, ["standard", "large", "largest"], "standard");

/** Before the first paint: main.tsx. */
export function applyStoredLook() {
  document.documentElement.dataset["look"] = storedLook();
  document.documentElement.dataset["text"] = storedTextSize();
}

/** A change, faded across the whole page where the browser can. */
function fade(change: () => void) {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
  if (!doc.startViewTransition || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    change();
    return;
  }
  doc.startViewTransition(change).ready.catch(() => undefined);
}

export function useLook(): [Look, (look: Look) => void] {
  const [look, setLook] = useState<Look>(storedLook);
  return [
    look,
    (next) => {
      write(LOOK_KEY, next);
      fade(() => {
        document.documentElement.dataset["look"] = next;
        setLook(next);
      });
    },
  ];
}

export function useTextSize(): [TextSize, (size: TextSize) => void] {
  const [size, setSize] = useState<TextSize>(storedTextSize);
  return [
    size,
    (next) => {
      write(TEXT_KEY, next);
      fade(() => {
        document.documentElement.dataset["text"] = next;
        setSize(next);
      });
    },
  ];
}

/** Whether the glass look is on now, for the few things drawn differently under it. */
export const isGlass = () => document.documentElement.dataset["look"] === "glass";

// What makes the glass bend what is behind it, as Billing Tracker's does: a map
// that pushes the view inwards near the edges and leaves the middle alone, as a
// thick pane would. Red moves it sideways and green up and down; mid-grey is
// no movement.
const LENS_MAP = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" preserveAspectRatio="none"><defs><linearGradient id="r"><stop offset="0" stop-color="#f00"/><stop offset=".05" stop-color="#800000"/><stop offset=".95" stop-color="#800000"/><stop offset="1" stop-color="#000"/></linearGradient><linearGradient id="g" x2="0" y2="1"><stop offset="0" stop-color="#0f0"/><stop offset=".3" stop-color="#008000"/><stop offset=".7" stop-color="#008000"/><stop offset="1" stop-color="#000"/></linearGradient></defs><rect width="100" height="100" fill="url(#r)"/><rect width="100" height="100" fill="url(#g)" style="mix-blend-mode:screen"/></svg>`;

/** The lens the glass bar and dialogs refract through: drawn once, in the shell. */
export function GlassLens() {
  // Kept in step with a change made on My account in another tab, too.
  const [, bump] = useState(0);
  useEffect(() => {
    const on = (e: StorageEvent) => {
      if (e.key === LOOK_KEY || e.key === TEXT_KEY) {
        applyStoredLook();
        bump((n) => n + 1);
      }
    };
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <filter id="pos-lens" x="0" y="0" width="1" height="1" colorInterpolationFilters="sRGB">
        <feImage href={`data:image/svg+xml,${encodeURIComponent(LENS_MAP)}`} preserveAspectRatio="none" result="map" />
        <feDisplacementMap in="SourceGraphic" in2="map" scale="46" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </svg>
  );
}
